import { useCallback, useMemo, useState } from "react";
import { formatAssetLabel } from "@/lib/marketUtils";
import { Market } from "@/lib/wsTypes";

export type TradeSide = "buy" | "sell";
export type OrderType = "market" | "limit";

/** The user's market choice, per tab. TradingView unmounts whenever the app
 * falls back to the deposits view (e.g. during a WS reconnect), and selection
 * state dies with it -- without this, returning to the trade view silently
 * reset the market to the URL params or the first market in the list. A fresh
 * tab (e.g. a shared ?sell=&buy= link) has no stored value, so the URL still
 * wins there. */
const SELECTED_MARKET_STORAGE_KEY = "sr:selected-market";

function readStoredMarketKey(): string | null {
  try {
    return sessionStorage.getItem(SELECTED_MARKET_STORAGE_KEY);
  } catch {
    return null;
  }
}

function getMarketUrlState(market: Market | null, tradeSide: TradeSide) {
  const params = new URLSearchParams(window.location.search);

  if (market) {
    const baseLabel = formatAssetLabel(market.tradingPair.base);
    const quoteLabel = formatAssetLabel(market.tradingPair.quote);
    params.set("sell", tradeSide === "sell" ? baseLabel : quoteLabel);
    params.set("buy", tradeSide === "sell" ? quoteLabel : baseLabel);
  } else {
    params.delete("sell");
    params.delete("buy");
  }

  const qs = params.toString();
  return qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
}

export function useTradingViewState(markets: readonly Market[], initialSelectedKey?: string | null) {
  const [orderType, setOrderType] = useState<OrderType>("market");
  const [limitPrice, setLimitPrice] = useState("");
  const [initialUrlSell] = useState(() => new URLSearchParams(window.location.search).get("sell"));
  const [initialUrlBuy] = useState(() => new URLSearchParams(window.location.search).get("buy"));
  const initialMarket = useMemo(() => {
    if (!initialUrlSell || !initialUrlBuy) return null;

    return markets.find((market) => {
      const baseLabel = formatAssetLabel(market.tradingPair.base);
      const quoteLabel = formatAssetLabel(market.tradingPair.quote);

      return (
        (baseLabel === initialUrlSell && quoteLabel === initialUrlBuy) ||
        (quoteLabel === initialUrlSell && baseLabel === initialUrlBuy)
      );
    }) ?? null;
  }, [initialUrlBuy, initialUrlSell, markets]);

  const [selectedMarketKey, setSelectedMarketKey] = useState<string | null>(initialSelectedKey ?? null);
  const [tradeSideOverride, setTradeSideOverride] = useState<TradeSide | null>(null);

  const selectedMarket = useMemo(() => {
    if (selectedMarketKey) {
      const explicitMarket = markets.find((market) => market.key === selectedMarketKey);
      if (explicitMarket) return explicitMarket;
    }

    return initialMarket ?? markets[0] ?? null;
  }, [initialMarket, markets, selectedMarketKey]);

  const tradeSide = useMemo<TradeSide>(() => {
    if (tradeSideOverride) return tradeSideOverride;
    if (!initialMarket || !initialUrlSell) return "sell";
    const baseLabel = formatAssetLabel(initialMarket.tradingPair.base);
    return initialUrlSell === baseLabel ? "sell" : "buy";
  }, [initialMarket, initialUrlSell, tradeSideOverride]);

  const syncUrl = useCallback((market: Market | null, nextTradeSide: TradeSide) => {
    window.history.replaceState(null, "", getMarketUrlState(market, nextTradeSide));
  }, []);

  const handleSelectMarket = useCallback((market: Market | null) => {
    if (!market) return;
    setSelectedMarketKey(market.key);
    try {
      sessionStorage.setItem(SELECTED_MARKET_STORAGE_KEY, market.key);
    } catch {
      /* private mode etc. -- selection just won't survive a remount */
    }
    syncUrl(market, tradeSide);
  }, [syncUrl, tradeSide]);

  const handleTradeSideChange = useCallback((nextTradeSide: TradeSide) => {
    setTradeSideOverride(nextTradeSide);
    syncUrl(selectedMarket, nextTradeSide);
  }, [selectedMarket, syncUrl]);

  const handleSelectPrice = useCallback((price: number) => {
    setOrderType("limit");
    setLimitPrice(price.toFixed(6).replace(/0+$/, "").replace(/\.$/, ""));
  }, []);

  return {
    selectedMarket,
    handleSelectMarket,
    tradeSide,
    setTradeSide: handleTradeSideChange,
    orderType,
    setOrderType,
    limitPrice,
    setLimitPrice,
    handleSelectPrice,
  };
}
